import { useState } from "react";

import { Link } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SUPPORT_CONTACT_EMAIL } from "@/lib/errors/root-error-catalog";
import { resolveRootError } from "@/lib/errors/resolve-root-error";
import { copyToClipboard } from "@/lib/ui-helper/clipboard";
import { HomeIcon } from "lucide-react";

export function RootErrorView({ error, reset }: ErrorComponentProps) {
  const resolved = resolveRootError(error);
  const [copied, setCopied] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(resolved.technicalDetail);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-red-50">
      <div className="w-full max-w-md space-y-4 bg-card shadow-xl mb-4">
        <h1 className="text-2xl font-bold text-foreground">{resolved.title}</h1>
        <p className="text-secondary text-sm font-bold whitespace-pre-line">
          {resolved.message}
        </p>
        <p className="text-sm text-muted-foreground">
          {resolved.supportFooter}{" "}
          <a
            href={`mailto:${SUPPORT_CONTACT_EMAIL}`}
            className="text-secondary underline-offset-4 hover:underline">
            {SUPPORT_CONTACT_EMAIL}
          </a>
        </p>

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={reset}>
            Réessayer la navigation
          </Button>
          <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
            <DialogTrigger
              render={<Button type="button" variant="secondary" />}>
              Voir les détails
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold">
                  Détail technique
                </DialogTitle>
                <DialogDescription>
                  Informations utiles à joindre dans un message à{" "}
                  {SUPPORT_CONTACT_EMAIL}.
                </DialogDescription>
              </DialogHeader>
              <pre className="max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap wrap-break-word border">
                {resolved.technicalDetail || "Aucun détail disponible."}
              </pre>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCopy}>
                  {copied ? "Copié" : "Copier les détails"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Button
        type="button"
        variant="link"
        nativeButton={false}
        render={<Link to="/" />}>
        <HomeIcon className="w-4 h-4" />
        Retour à la page d&apos;accueil
      </Button>
    </div>
  );
}
